import React, { useEffect, useMemo } from "react";

import { Fade, InputAdornment } from "@mui/material";
import { add, sub } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import ms from "ms";
import { useFormContext } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";

import useGetTaskActivityLog from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import {
  TaskComment,
  TaskDetailsV3,
  TaskPriority,
  TaskStatus,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  AdvisorId,
  AdvisorRequestConfig,
} from "@fartherfinance/frontend/api/Types";

import {
  assigneeDefaultOption,
  custodianOptions,
  MIN_MAX_DUE_DATE_RANGE_YEARS,
  TaskAssigneeAutocompleteOption,
  taskPriorityOptions,
} from "../../../common";
import {
  useAdvisorsOptions,
  useAssigneesOptions,
  useClientsOptions,
  useServiceTeamsOptions,
  useTaskStatusOptionsV2,
  useTaskTypeOptionsV2,
} from "../../../hooks";
import {
  TaskPermissionsProvider,
  useTaskPermissions,
} from "../../../taskPermissionsContext";
import TaskPriorityIcon from "../../../TaskPriorityIcon/TaskPriorityIcon";
import TaskStatusIcon from "../../../TaskStatusIcon/TaskStatusIcon";
import { isDateValid, isTaskFieldEditingRestricted } from "../../../utils";
import TaskActivity from "../TaskActivity/TaskActivity";
import TaskActivityV2 from "../TaskActivity/TaskActivityV2";
import TaskAttachments from "../TaskAttachments/TaskAttachments";
import TaskDescriptionV3 from "../TaskDescription/TaskDescriptionV3";
import { type TaskDetailsFormDataV3 } from "../TaskDetailsFormProvider/TaskDetailsFormProvider.types";
import TaskSubtasks from "../TaskSubtasks/TaskSubtasks";
import TaskTitleV3 from "../TaskTitle/TaskTitleV3";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import {
  EnhancedAutocompleteOption,
  WithGroupMeta,
} from "@src/multiCustodian/pages/Advisor/common";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import FormBody from "@src/sharedComponents/Forms/FormBody";
import FormDateField from "@src/sharedComponents/Forms/FormDateField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./TaskDetailsUI.module.css";

interface TaskDetailsUIProps {
  taskDetails: TaskDetailsV3;
  taskComments: TaskComment[];
  isEmbedded?: boolean;
}

const TaskDetailsUIInternal: React.FC<TaskDetailsUIProps> = ({
  taskDetails,
  taskComments,
  isEmbedded,
}) => {
  const {
    enableTaskServiceTeams,
    enableTaskSubtasks,
    enableTaskActivityLogV2,
  } = useFlags();

  const history = useHistory();
  const auth = useAdvisorRequestAuth();
  const { clientsOptions, clientsQuery } = useClientsOptions();
  const { advisorsOptions, advisorsQuery } = useAdvisorsOptions();
  const { serviceTeamsOptions, serviceTeamsQuery } = useServiceTeamsOptions();
  const { data: taskActivities, refetchTaskActivityLog } =
    useGetTaskActivityLog(taskDetails.id, auth as AdvisorRequestConfig);

  const form = useFormContext<TaskDetailsFormDataV3>();
  const [formServiceTeam, formAssignee, formTaskStatus, formTaskPriority] =
    form.watch(["serviceTeam", "assignee", "taskStatus", "taskPriority"]);

  const { taskTypeFormOptions } = useTaskTypeOptionsV2(
    taskDetails?.taskStatus,
    taskDetails?.isSystemGenerated
  );
  const { taskStatusFormOptions } = useTaskStatusOptionsV2(
    taskDetails?.taskType
  );

  const assigneesQueryParams = useMemo(() => {
    return taskDetails
      ? {
          ...(taskDetails.serviceTeamId && {
            serviceTeam: taskDetails.serviceTeamId,
          }),
          ...(taskDetails.advisor && {
            advisor: taskDetails.advisor.id as AdvisorId,
          }),
        }
      : undefined;
  }, [taskDetails]);

  const { assigneesOptions, assigneesQuery } = useAssigneesOptions(
    assigneesQueryParams,
    true
  );
  const extendedAssigneesOptions = useMemo(
    () => [assigneeDefaultOption, ...assigneesOptions],
    [assigneesOptions]
  );

  const isServiceTeamFieldVisibleForCX = Boolean(enableTaskServiceTeams);
  const dueDates = useMemo(() => {
    return {
      min: sub(new Date(), { years: MIN_MAX_DUE_DATE_RANGE_YEARS }),
      max: add(new Date(), { years: MIN_MAX_DUE_DATE_RANGE_YEARS }),
    };
  }, []);

  const handleClearAutocompleteField = (
    field: keyof TaskDetailsFormDataV3
  ): void => {
    form.setValue(field, null);
  };

  const { taskPermissions } = useTaskPermissions();
  const {
    isAllowedToUpdate: isAllowedToEditTask,
    isAllowedToUpdateMetadata: isAllowedToEditTaskMetadata,
  } = taskPermissions(taskDetails);

  useEffect(() => {
    if (!enableTaskActivityLogV2) {
      return;
    }

    const intervalId = setInterval(() => {
      refetchTaskActivityLog();
    }, ms("30seconds"));

    return () => clearInterval(intervalId);
  }, [enableTaskActivityLogV2, refetchTaskActivityLog]);

  const isRaiseCashRequestTaskClosed =
    taskDetails.taskType === "LIQUIDATION" &&
    (taskDetails.taskStatus === "COMPLETED" ||
      taskDetails.taskStatus === "CANCELED");

  return (
    <Fade key={taskDetails.id} in>
      <div className={styles.container}>
        <div className={styles.header}>
          <TaskTitleV3 taskDetails={taskDetails} />

          <div>
            {enableTaskSubtasks && taskDetails.parent && (
              <Link
                className={styles.parentTaskLink}
                to={{
                  pathname: `/Advisor/Tasks/${taskDetails.parent.id}`,
                  search: history.location.search,
                }}
              >
                <Text variant="body" color="textSubtle">
                  Parent Task: {taskDetails.parent.title}
                </Text>
              </Link>
            )}

            <TaskDescriptionV3 task={taskDetails} />
          </div>
        </div>

        <div className={styles.sections}>
          <div>
            <FormBody>
              <FormDropdownField<
                EnhancedAutocompleteOption<TaskStatus> & WithGroupMeta
              >
                name="taskStatus"
                label="Status"
                values={taskStatusFormOptions}
                groupBy={(option) => option.meta.group}
                disabled={
                  isRaiseCashRequestTaskClosed ||
                  !isAllowedToEditTaskMetadata ||
                  isTaskFieldEditingRestricted("taskStatus", taskDetails)
                }
                getOptionKey={(option) => option.value}
                renderOption={(optionProps, option) => (
                  <li
                    {...optionProps}
                    className={toClassName(
                      optionProps.className,
                      styles.option
                    )}
                  >
                    <TaskStatusIcon taskStatus={option.value} />
                    <Text variant="body">{option.label}</Text>
                  </li>
                )}
                renderStartAdornment={() => (
                  <InputAdornment position="start">
                    <TaskStatusIcon taskStatus={formTaskStatus.value} />
                  </InputAdornment>
                )}
              />

              <FormDropdownField<EnhancedAutocompleteOption<TaskPriority>>
                name="taskPriority"
                label="Priority"
                values={taskPriorityOptions}
                disabled={
                  !isAllowedToEditTaskMetadata ||
                  isTaskFieldEditingRestricted("taskPriority", taskDetails)
                }
                getOptionKey={(option) => option.value}
                renderOption={(optionProps, option) => (
                  <li
                    {...optionProps}
                    className={toClassName(
                      optionProps.className,
                      styles.option
                    )}
                  >
                    <TaskPriorityIcon value={option.value} />
                    <Text variant="body">{option.label}</Text>
                  </li>
                )}
                renderStartAdornment={() => (
                  <InputAdornment position="start">
                    <TaskPriorityIcon value={formTaskPriority.value} />
                  </InputAdornment>
                )}
              />
            </FormBody>
          </div>

          <div>
            <Text variant="title3" className={styles.sectionHeader}>
              Details
            </Text>

            <FormBody>
              <FormDropdownField<EnhancedAutocompleteOption>
                name="taskType"
                label="Type"
                values={taskTypeFormOptions}
                disabled={
                  !isAllowedToEditTask ||
                  isTaskFieldEditingRestricted("taskType", taskDetails)
                }
                getOptionKey={(option) => option.value}
              />

              {isServiceTeamFieldVisibleForCX ? (
                <>
                  <FormDropdownField<EnhancedAutocompleteOption>
                    name="serviceTeam"
                    label={
                      formAssignee && formAssignee.value
                        ? "Service Team (Optional)"
                        : "Service Team"
                    }
                    values={serviceTeamsOptions}
                    required={
                      formAssignee && formAssignee.value
                        ? false
                        : "Please select a service team"
                    }
                    disabled={
                      !isAllowedToEditTask ||
                      isTaskFieldEditingRestricted("serviceTeam", taskDetails)
                    }
                    isLoading={serviceTeamsQuery.isLoading}
                    getOptionKey={(option) => option.value}
                    onClear={
                      formAssignee &&
                      formAssignee.value &&
                      !serviceTeamsQuery.isLoading
                        ? () => handleClearAutocompleteField("serviceTeam")
                        : undefined
                    }
                  />

                  <FormDropdownField<EnhancedAutocompleteOption>
                    name="advisor"
                    label="Advisor"
                    values={advisorsOptions}
                    disabled={
                      !isAllowedToEditTask ||
                      isTaskFieldEditingRestricted("advisor", taskDetails)
                    }
                    isLoading={advisorsQuery.isLoading}
                    getOptionKey={(option) => option.value}
                  />

                  <FormDropdownField<
                    TaskAssigneeAutocompleteOption<string | undefined>
                  >
                    name="assignee"
                    label={formServiceTeam ? "Assignee (Optional)" : "Assignee"}
                    values={extendedAssigneesOptions}
                    required={
                      formServiceTeam ? false : "Please select an assignee"
                    }
                    disabled={
                      !isAllowedToEditTaskMetadata ||
                      isTaskFieldEditingRestricted("assignee", taskDetails)
                    }
                    isLoading={assigneesQuery.isLoading}
                    getOptionKey={(option) => option.value ?? "undefined"}
                    onClear={
                      formServiceTeam && !assigneesQuery.isLoading
                        ? () => handleClearAutocompleteField("assignee")
                        : undefined
                    }
                  />
                </>
              ) : (
                <>
                  <FormDropdownField<EnhancedAutocompleteOption>
                    name="advisor"
                    label="Advisor"
                    values={advisorsOptions}
                    disabled={
                      !isAllowedToEditTask ||
                      isTaskFieldEditingRestricted("advisor", taskDetails)
                    }
                    isLoading={advisorsQuery.isLoading}
                    getOptionKey={(option) => option.value}
                  />

                  <FormDropdownField<
                    TaskAssigneeAutocompleteOption<string | undefined>
                  >
                    name="assignee"
                    label="Assignee"
                    values={extendedAssigneesOptions}
                    disabled={
                      !isAllowedToEditTaskMetadata ||
                      isTaskFieldEditingRestricted("assignee", taskDetails)
                    }
                    isLoading={assigneesQuery.isLoading}
                    getOptionKey={(option) => option.value ?? "undefined"}
                  />
                </>
              )}

              <FormDropdownField<EnhancedAutocompleteOption>
                name="client"
                label="Client (Optional)"
                values={clientsOptions}
                disabled={
                  !isAllowedToEditTask ||
                  isTaskFieldEditingRestricted("client", taskDetails)
                }
                isLoading={clientsQuery.isLoading}
                getOptionKey={(option) => option.value}
                onClear={
                  !clientsQuery.isLoading
                    ? () => handleClearAutocompleteField("client")
                    : undefined
                }
              />

              <FormDateField
                name="dueDate"
                label="Due Date"
                placeholder=""
                helperText="MM/DD/YYYY"
                minDate={dueDates.min}
                maxDate={dueDates.max}
                required="Please select a due date"
                disabled={
                  !isAllowedToEditTaskMetadata ||
                  isTaskFieldEditingRestricted("dueDate", taskDetails)
                }
                rules={{
                  validate: { isDateValid },
                }}
              />

              <FormDropdownField<EnhancedAutocompleteOption>
                name="custodian"
                label="Custodian (Optional)"
                values={custodianOptions}
                disabled={
                  !isAllowedToEditTask ||
                  isTaskFieldEditingRestricted("custodian", taskDetails)
                }
                getOptionKey={(option) => option.value}
                onClear={() => handleClearAutocompleteField("custodian")}
              />
            </FormBody>
          </div>

          {taskDetails.attachments.length > 0 && (
            <div>
              <Text variant="title3" className={styles.sectionHeader}>
                Attachments
              </Text>

              <TaskAttachments taskDetails={taskDetails} />
            </div>
          )}

          {enableTaskSubtasks && <TaskSubtasks taskDetails={taskDetails} />}
        </div>

        {enableTaskActivityLogV2 ? (
          <TaskActivityV2
            taskDetails={taskDetails}
            isEmbedded={isEmbedded}
            taskActivities={taskActivities}
          />
        ) : (
          <TaskActivity
            taskDetails={taskDetails}
            taskComments={taskComments}
            isEmbedded={isEmbedded}
          />
        )}
      </div>
    </Fade>
  );
};

const TaskDetailsUIV3: React.FC<TaskDetailsUIProps> = (props) => {
  const { taskComments, taskDetails } = props;

  const taskRelatedAttachments = useMemo(
    () => [
      ...taskDetails.attachments,
      ...taskComments.map((c) => c.attachments).flatMap((a) => a),
    ],
    [taskComments, taskDetails]
  );

  return (
    <TaskPermissionsProvider
      comments={taskComments}
      taskId={taskDetails.id}
      attachments={taskRelatedAttachments}
    >
      <TaskDetailsUIInternal {...props} />
    </TaskPermissionsProvider>
  );
};

export default TaskDetailsUIV3;
